import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { Observable } from 'rxjs';
import { filter, map, mergeMap } from 'rxjs/operators';

import { TitleService } from './modules/shared/services/title.service';
import { IdentificationProviderService, User } from './modules/auth/identification-provider.service';
import {
  SUPPORTED_LANGUAGES,
  FALLBACK_LANGUAGE,
} from './modules/shared/constants/languages.constant';
import { Language } from './modules/shared/interfaces/language.interface';
import { DEFAULT } from '@shared/enums/accepted-language.enum';

@Component({
  selector: 'form-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  public title: string;
  public supportedLanguages: Language[] = SUPPORTED_LANGUAGES;
  public currentLang: string;
  public user$: Observable<User | null>

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: TitleService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
    private identificationProviderService: IdentificationProviderService
  ) {}

  ngOnInit(): void {
    this.translateService.setDefaultLang(FALLBACK_LANGUAGE);
    let currentLang = localStorage.getItem('currentLang');

    if (!currentLang) {
      const browserLang = this.translateService.getBrowserLang()?? DEFAULT;
      if (this.isSupportedLanguage(browserLang)) {
        currentLang = browserLang;
      } else {
        currentLang = FALLBACK_LANGUAGE;
      }
    }

    this.translateService.use(currentLang);

    this.translateService.onLangChange.subscribe((e: LangChangeEvent) => {
      this.currentLang = e.lang;
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        mergeMap((route) => route.data)
      )
      .subscribe((event) => this.titleService.setTitle(event.title));

    this.titleService.getTitle().subscribe((title: string) => {
      this.title = title;
      this.cdr.markForCheck();
    });

    this.user$ = this.identificationProviderService.getUser();
  }

  changeLanguage(language: Language): void {
    this.translateService.use(language.code);
    localStorage.setItem('currentLang', language.code);
  }

  logout(): void {
    this.identificationProviderService.logOut();
  }

  private isSupportedLanguage(languageCode: string): boolean {
    return SUPPORTED_LANGUAGES.map((language) => language.code).includes(
      languageCode
    );
  }
}
